import React, { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Link,
  useParams,
} from "react-router";
import { useTranslation } from "react-i18next";
import "./App.scss";
import useFirebaseInit from "./hooks/useFirebaseInit";
import FirebaseErrorBoundary from "./components/ErrorBoundary/FirebaseErrorBoundary";
import SharedGroceryList, {
  SharedGroceryListEmptyState,
  SharedGroceryListLoadingShell,
} from "./components/SharedGroceryList/SharedGroceryList";
import {
  analyticsParticipation,
  type WebAnalyticsParticipation,
} from "./productAnalytics";

const legalLinks = {
  privacyDe: "https://legal.pantryverse.com/privacy/de",
  privacyEn: "https://legal.pantryverse.com/privacy/en",
  termsDe: "https://legal.pantryverse.com/terms/de",
  termsEn: "https://legal.pantryverse.com/terms/en",
  imprintDe: "https://legal.pantryverse.com/impressum",
  imprintEn: "https://legal.pantryverse.com/imprint",
};

const legalLinkItems = {
  de: [
    { href: legalLinks.privacyDe, labelKey: "legal.links.privacy" },
    { href: legalLinks.termsDe, labelKey: "legal.links.terms" },
    { href: legalLinks.imprintDe, labelKey: "legal.links.imprint" },
  ],
  en: [
    { href: legalLinks.privacyEn, labelKey: "legal.links.privacy" },
    { href: legalLinks.termsEn, labelKey: "legal.links.terms" },
    { href: legalLinks.imprintEn, labelKey: "legal.links.imprint" },
  ],
};

// Firebase error component
const FirebaseErrorComponent: React.FC<{ error: string }> = ({ error }) => {
  const { t } = useTranslation();

  return (
    <div
      style={{
        padding: "var(--spacing-scale-400)",
        textAlign: "center",
        backgroundColor: "var(--color-background-secondary)",
        border: "1px solid var(--color-border-error)",
        borderRadius: "var(--radius-m)",
        margin: "var(--spacing-scale-400)",
      }}
    >
      <h2 style={{ color: "var(--color-text-error)" }}>
        {t("app.firebase_error_title")}
      </h2>
      <p style={{ color: "var(--color-text-secondary)" }}>
        {t("app.firebase_error_body")}
      </p>
      {import.meta.env.DEV && error && (
        <p style={{ color: "var(--color-text-secondary)" }}>{error}</p>
      )}
      <button
        onClick={() => window.location.reload()}
        style={{
          backgroundColor: "var(--color-interactive-primary-default)",
          color: "var(--color-text-on-primary)",
          border: "none",
          padding: "var(--spacing-scale-200) var(--spacing-scale-300)",
          borderRadius: "var(--radius-s)",
          cursor: "pointer",
          marginTop: "var(--spacing-scale-200)",
        }}
      >
        {t("common.retry")}
      </button>
    </div>
  );
};

// Enhanced welcome page with preloading triggers
const WelcomePage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <div style={{ padding: "2rem", textAlign: "center" }}>
      <h1>{t("welcome.title")}</h1>
      <p>{t("welcome.subtitle")}</p>
      <div style={{ marginTop: "2rem" }}>
        <Link
          to="/list/demo"
          style={{
            display: "inline-block",
            padding: "12px 24px",
            backgroundColor: "var(--color-interactive-primary-default)",
            color: "var(--color-text-on-primary)",
            textDecoration: "none",
            borderRadius: "var(--radius-m)",
            fontWeight: "var(--font-weight-medium)",
          }}
        >
          {t("welcome.demo_link")}
        </Link>
      </div>
      <p
        style={{
          marginTop: "1rem",
          fontSize: "0.875rem",
          color: "var(--color-text-secondary)",
        }}
      >
        {t("welcome.preload_hint")}
      </p>
    </div>
  );
};

const AcquisitionInterestPage: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="app-redirect acquisition-interest">
      <p className="acquisition-interest-eyebrow">
        {t("acquisitionInterest.eyebrow")}
      </p>
      <h1>{t("acquisitionInterest.title")}</h1>
      <p>{t("acquisitionInterest.body")}</p>
      <p>{t("acquisitionInterest.follow_up")}</p>
      <Link className="app-link" to="/">
        {t("acquisitionInterest.back")}
      </Link>
    </section>
  );
};

const HouseholdInvitationInstallLanding: React.FC = () => {
  const { inviteId } = useParams();
  const { t } = useTranslation();
  const [landing] = useState(() => {
    const fragment = window.location.hash.slice(1);
    const match = fragment.match(
      /^v=1&s=([A-Za-z0-9_-]{43})&c=([A-HJ-NP-Z2-9]{16})$/,
    );
    window.history.replaceState(
      window.history.state,
      "",
      `/household-invite/${inviteId ?? "unavailable"}`,
    );
    const compactCode = match?.[2];
    return {
      capabilityWasPresent: match !== null,
      recoveryCode: compactCode?.match(/.{4}/g)?.join("-") ?? null,
    };
  });

  const validRoute =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(
      inviteId ?? "",
    );
  const recoveryCode = landing.recoveryCode;

  return (
    <section className="household-invitation-landing">
      <p className="household-invitation-eyebrow">
        {t("householdInvitation.eyebrow")}
      </p>
      <h1>{t("householdInvitation.title")}</h1>
      <p>{t("householdInvitation.body")}</p>
      {validRoute && landing.capabilityWasPresent && recoveryCode ? (
        <>
          <p>{t("householdInvitation.copy_before_install")}</p>
          <output
            className="household-invitation-code"
            aria-label={t("householdInvitation.code_label") as string}
          >
            {recoveryCode}
          </output>
          <button
            type="button"
            className="household-invitation-copy"
            onClick={() => {
              void navigator.clipboard.writeText(recoveryCode).catch(() => {});
            }}
          >
            {t("householdInvitation.copy_code")}
          </button>
          <a
            className="household-invitation-testflight"
            href="https://testflight.apple.com/join/zGzArA7B"
            rel="noreferrer"
          >
            {t("householdInvitation.testflight")}
          </a>
        </>
      ) : (
        <p role="status">{t("householdInvitation.unavailable")}</p>
      )}
      <p>{t("householdInvitation.best_effort")}</p>
    </section>
  );
};

const LegalFooter: React.FC<{ onOpenAnalyticsPreferences: () => void }> = ({
  onOpenAnalyticsPreferences,
}) => {
  const { t, i18n } = useTranslation();
  const language = i18n?.resolvedLanguage ?? i18n?.language ?? "en";
  const links = legalLinkItems[language.startsWith("de") ? "de" : "en"];

  return (
    <footer
      className="legal-footer"
      aria-label={t("legal.footer_label") as string}
    >
      <div className="legal-links">
        {links.map((link) => (
          <a key={link.href} href={link.href} target="_blank" rel="noreferrer">
            {t(link.labelKey)}
          </a>
        ))}
        <button
          type="button"
          className="legal-link-button"
          onClick={onOpenAnalyticsPreferences}
        >
          {t("analytics.preferences.button")}
        </button>
      </div>
    </footer>
  );
};

const AnalyticsConsentControls: React.FC<{
  store: WebAnalyticsParticipation;
  preferencesOpen: boolean;
  onClosePreferences: () => void;
}> = ({ store, preferencesOpen, onClosePreferences }) => {
  const { t, i18n } = useTranslation();
  const [consent, setConsent] = useState(store.current);

  useEffect(() => store.subscribe(setConsent), [store]);

  const title = t("analytics.consent.title") as string;
  const language = i18n?.resolvedLanguage ?? i18n?.language ?? "en";
  const privacyUrl = language.startsWith("de")
    ? legalLinks.privacyDe
    : legalLinks.privacyEn;
  const showInitialChoice = consent === "unknown";

  if (!showInitialChoice && !preferencesOpen) return null;

  return (
    <section
      className="analytics-consent"
      aria-label={
        showInitialChoice ? title : (t("analytics.preferences.title") as string)
      }
    >
      <div className="analytics-consent-copy">
        <h2>
          {t(
            showInitialChoice
              ? "analytics.consent.title"
              : "analytics.preferences.title",
          )}
        </h2>
        <p>
          {showInitialChoice
            ? t("analytics.consent.body")
            : t(
                consent === "granted"
                  ? "analytics.preferences.granted"
                  : "analytics.preferences.denied",
              )}{" "}
          <a href={privacyUrl} target="_blank" rel="noreferrer">
            {t("analytics.consent.privacy_link")}
          </a>
        </p>
      </div>
      <div className="analytics-consent-actions">
        <button type="button" onClick={() => store.set("granted")}>
          {t("analytics.consent.allow")}
        </button>
        <button type="button" onClick={() => store.set("denied")}>
          {t("analytics.consent.deny")}
        </button>
        {preferencesOpen && (
          <button type="button" onClick={onClosePreferences}>
            {t("analytics.preferences.close")}
          </button>
        )}
      </div>
    </section>
  );
};

interface AppProps {
  analyticsConsentStore?: WebAnalyticsParticipation;
}

const App: React.FC<AppProps> = ({
  analyticsConsentStore = analyticsParticipation,
}) => {
  const { isLoading, error } = useFirebaseInit();
  const [analyticsPreferencesOpen, setAnalyticsPreferencesOpen] =
    useState(false);

  // Show loading state during Firebase initialization
  if (isLoading) {
    return <SharedGroceryListLoadingShell />;
  }

  // Show error state if Firebase initialization failed
  if (error) {
    return <FirebaseErrorComponent error={error} />;
  }

  // Main app render with Firebase error boundary
  return (
    <FirebaseErrorBoundary>
      <Router>
        <div className="app-shell">
          <main className="app-content">
            <Routes>
              <Route path="/list/:listId" element={<SharedGroceryList />} />
              <Route
                path="/household-invite/:inviteId"
                element={<HouseholdInvitationInstallLanding />}
              />
              {import.meta.env.VITE_E2E_TESTING === "true" && (
                <Route
                  path="/e2e/shared-list-empty"
                  element={<SharedGroceryListEmptyState />}
                />
              )}
              <Route path="/download" element={<AcquisitionInterestPage />} />
              <Route path="/" element={<WelcomePage />} />
            </Routes>
          </main>
          <LegalFooter
            onOpenAnalyticsPreferences={() => setAnalyticsPreferencesOpen(true)}
          />
          <AnalyticsConsentControls
            store={analyticsConsentStore}
            preferencesOpen={analyticsPreferencesOpen}
            onClosePreferences={() => setAnalyticsPreferencesOpen(false)}
          />
        </div>
      </Router>
    </FirebaseErrorBoundary>
  );
};

export default App;
